// ============================================================================
// EXPENSE TYPE FORM MODAL — Add / Edit Expense Type (MSTR-EXPTYPE-001)
// ============================================================================
// Adds is_reimbursable toggle and gl_code text field to the standard A-tier shape.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { expenseTypeService } from 'api/services/expenseTypeService';
import type { ExpenseType } from 'api/types/expenseType.types';

type ExpenseStatus = 'ACTIVE' | 'INACTIVE';

interface ExpenseTypeFormData {
  expenseCode: string;
  expenseName: string;
  glCode: string;
  isReimbursable: boolean;
  statusCode: ExpenseStatus;
}

const INITIAL_FORM_DATA: ExpenseTypeFormData = {
  expenseCode: '',
  expenseName: '',
  glCode: '',
  isReimbursable: true,
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export interface ExpenseTypeFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: ExpenseType | null;
  onSaved: () => void;
  onClose: () => void;

  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const ExpenseTypeFormModal: React.FC<ExpenseTypeFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<ExpenseTypeFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const recordRef = useRef(record);
  recordRef.current = record;

  const initialSnapshotRef = useRef('');

  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: ExpenseTypeFormData = rec
      ? {
          expenseCode: rec.expenseCode,
          expenseName: rec.expenseName,
          glCode: rec.glCode ?? '',
          isReimbursable: rec.isReimbursable,
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as ExpenseStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.expenseCode || currentFormData.expenseCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.expenseCode?.trim();
      const name = currentFormData.expenseName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.expenseCode || currentRecord?.expenseCode || 'Expense Type'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.expenseCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.expenseCode.trim();
    const name = formData.expenseName.trim();

    if (!code) errors.expenseCode = 'Expense code is required';
    else if (code.length > 30) errors.expenseCode = 'Max 30 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.expenseCode = 'Uppercase letters, digits, hyphens only';

    if (!name) errors.expenseName = 'Expense name is required';
    else if (name.length > 100) errors.expenseName = 'Max 100 characters';

    if (formData.glCode.trim().length > 30) errors.glCode = 'Max 30 characters';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.expenseCode.trim().toUpperCase();
      const name = formData.expenseName.trim();
      const glCode = formData.glCode.trim() || null;

      if (mode === 'create' || mode === 'copy') {
        await expenseTypeService.create({
          expenseCode: code,
          expenseName: name,
          glCode,
          isReimbursable: formData.isReimbursable,
          statusCode: formData.statusCode,
        });
        notificationService.toast.success(`Expense Type "${code}" created successfully.`);
      } else if (record) {
        await expenseTypeService.update(record.id, {
          expenseCode: code,
          expenseName: name,
          glCode,
          isReimbursable: formData.isReimbursable,
          statusCode: formData.statusCode,
          rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Expense Type "${code}" updated successfully.`);
      }

      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This expense type was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'An expense type with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        const serverErrors: string[] = err.response.data.errors;
        notificationService.toast.error(serverErrors.join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save expense type. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'view' ? 'View Expense Type' : mode === 'copy' ? 'Copy Expense Type' : mode === 'create' ? 'Add Expense Type' : 'Edit Expense Type'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-group" data-field="expenseCode">
            <label className="form-label">
              Expense Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('expenseCode') ? ' form-field-error' : ''}`}
              value={formData.expenseCode}
              maxLength={30}
              placeholder="COURT-FEE, ADV-FEE..."
              onChange={(e) => {
                clearFieldError('expenseCode');
                setFormData(prev => ({ ...prev, expenseCode: e.target.value.toUpperCase() }));
              }}
              disabled={isSaving}
            />
            {getFieldError('expenseCode') && (
              <div className="form-error-message">{getFieldError('expenseCode')}</div>
            )}
          </div>

          <div className="form-group" data-field="expenseName">
            <label className="form-label">
              Expense Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('expenseName') ? ' form-field-error' : ''}`}
              value={formData.expenseName}
              maxLength={100}
              placeholder="Court Fee"
              onChange={(e) => {
                clearFieldError('expenseName');
                setFormData(prev => ({ ...prev, expenseName: e.target.value }));
              }}
              disabled={isSaving}
            />
            {getFieldError('expenseName') && (
              <div className="form-error-message">{getFieldError('expenseName')}</div>
            )}
          </div>

          <div className="form-group" data-field="glCode">
            <label className="form-label">GL Code</label>
            <input
              type="text"
              className={`form-input${getFieldError('glCode') ? ' form-field-error' : ''}`}
              value={formData.glCode}
              maxLength={30}
              placeholder="GL-5110 (optional)"
              onChange={(e) => {
                clearFieldError('glCode');
                setFormData(prev => ({ ...prev, glCode: e.target.value }));
              }}
              disabled={isSaving}
            />
            {getFieldError('glCode') && (
              <div className="form-error-message">{getFieldError('glCode')}</div>
            )}
          </div>

          <div className="form-group" data-field="isReimbursable">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isReimbursable}
                onChange={(e) => setFormData(prev => ({ ...prev, isReimbursable: e.target.checked }))}
                disabled={isSaving}
                style={{ width: '1rem', height: '1rem' }}
              />
              Reimbursable (claimable from borrower)
            </label>
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(val) => setFormData(prev => ({
                ...prev,
                statusCode: ((val as string) || 'ACTIVE') as ExpenseStatus,
              }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          (mode === 'create' || mode === 'copy')
            ? `Create expense type "${formData.expenseCode.trim().toUpperCase()}"?`
            : `Update expense type "${formData.expenseCode.trim().toUpperCase()}"?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default ExpenseTypeFormModal;
